import {storySnapshot} from './storyVersions.js';

export const MAX_ASSEMBLY_CUTS=4;
export const MAIN_ASSEMBLY_CUT_ID='main';
const SNAPSHOT_FIELDS=['blocks','sectionHeadings','sourceColourOrder','targetRuntimeSeconds',
 'sequenceRate','aiStoryTitle','aiStorySummary','refine'];
const HISTORY_FIELDS=['storyVersions','aiCheckpoints'];
const clone=value=>JSON.parse(JSON.stringify(value));
const trimName=value=>String(value||'').trim().slice(0,80);

function historyOf(paper){
 return Object.fromEntries(HISTORY_FIELDS.map(key=>[key,clone(paper?.[key]||[])]));
}
function assertValid(cuts){
 if(!Array.isArray(cuts.items)||cuts.items.length<1||cuts.items.length>MAX_ASSEMBLY_CUTS
   ||!cuts.items.every(cut=>cut&&typeof cut.id==='string'&&cut.id&&
     typeof cut.name==='string'&&cut.name&&Array.isArray(cut.snapshot?.blocks))
   ||new Set(cuts.items.map(cut=>cut.id)).size!==cuts.items.length
   ||!cuts.items.some(cut=>cut.id===cuts.activeId))
  throw new Error('Saved Assembly cuts could not be read. Your edit was not changed.');
}
export function readAssemblyCuts(paper){
 const stored=paper?.assemblyCuts;
 if(stored){
  assertValid(stored);
  return stored;
 }
 return {activeId:MAIN_ASSEMBLY_CUT_ID,items:[{
  id:MAIN_ASSEMBLY_CUT_ID,name:'Main Cut',createdAt:null,updatedAt:null,
  snapshot:storySnapshot(paper),history:historyOf(paper)
 }]};
}
export function currentAssemblyCut(paper){
 const cuts=readAssemblyCuts(paper);
 return cuts.items.find(item=>item.id===cuts.activeId);
}
export function activeCutName(paper){return currentAssemblyCut(paper).name;}
export function assemblyCutExportSuffix(paper){
 const cuts=readAssemblyCuts(paper);
 if(cuts.items.length<2)return '';
 const title=activeCutName(paper).normalize('NFKD')
  .replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]+/gi,'-')
  .replace(/^-+|-+$/g,'').toLowerCase().slice(0,45)||'cut';
 return '-'+title;
}
export function assemblyCutDuration(snapshot){
 return (snapshot?.blocks||[]).reduce((total,block)=>
  total+Math.max(0,Number(block.end)-Number(block.start)||0),0);
}
function synced(paper,now){
 const cuts=readAssemblyCuts(paper);
 return {...cuts,items:cuts.items.map(item=>item.id===cuts.activeId
  ?{...item,snapshot:storySnapshot(paper),history:historyOf(paper),updatedAt:now||item.updatedAt}
  :item)};
}
// Keep the active working edit canonical for the existing player and NLE exporters.
// Alternative cuts never duplicate video, source transcripts or storage URLs.
export function syncActiveAssemblyCut(paper,{now=null}={}){
 if(!paper?.assemblyCuts)return paper;
 return {...paper,assemblyCuts:synced(paper,now)};
}
function applySavedCut(paper,snapshot,history){
 const cleaned={...paper};
 for(const field of [...SNAPSHOT_FIELDS,...HISTORY_FIELDS])delete cleaned[field];
 return {...cleaned,...clone(snapshot),...clone(history||{storyVersions:[],aiCheckpoints:[]})};
}
export function switchAssemblyCut(paper,id,{now=new Date().toISOString()}={}){
 const cuts=synced(paper,now);
 if(cuts.activeId===id)return paper;
 const target=cuts.items.find(item=>item.id===id);
 if(!target)throw new Error('That Assembly cut could not be found.');
 for(const block of target.snapshot.blocks){
  const source=paper.sources?.[block.sourceId];
  if(!source||!Number.isFinite(block.start)||!Number.isFinite(block.end)
    ||block.start<0||block.end<=block.start||block.end>(source.duration||0)+.02)
   throw new Error('This cut references unavailable or invalid source footage. Nothing was switched.');
 }
 const next=applySavedCut(paper,target.snapshot,target.history);
 return {...next,assemblyCuts:{activeId:id,items:cuts.items}};
}
export function renameAssemblyCut(paper,id,name,{now=new Date().toISOString()}={}){
 const title=trimName(name);
 if(!title)throw new Error('Enter a name for this cut.');
 const cuts=synced(paper,now);
 if(!cuts.items.some(item=>item.id===id))throw new Error('That Assembly cut could not be found.');
 if(cuts.items.find(item=>item.id===id).name===title)return paper;
 return {...paper,assemblyCuts:{...cuts,items:cuts.items.map(item=>
  item.id===id?{...item,name:title,updatedAt:now}:item)}};
}
export function duplicateAssemblyCut(paper,{id=()=>crypto.randomUUID(),now=new Date().toISOString()}={}){
 const cuts=synced(paper,now);
 if(cuts.items.length>=MAX_ASSEMBLY_CUTS)throw new Error('You can have up to four working cuts in one project.');
 const active=cuts.items.find(item=>item.id===cuts.activeId);
 const newId=id();
 if(!newId||cuts.items.some(item=>item.id===newId))throw new Error('Could not assign a unique ID to the new cut.');
 const snapshot=storySnapshot(paper);
 // Do not copy project commentary anchors onto a new editorial branch.
 // Fresh passage IDs keep client review notes on their original cut.
 snapshot.blocks=snapshot.blocks.map(block=>({...block,id:id()}));
 snapshot.sectionHeadings=(snapshot.sectionHeadings||[]).map(h=>({...h,id:id()}));
 delete snapshot.refine; // review/lock state belongs to the original cut
 const name=trimName(active.name+' copy');
 const newCut={id:newId,name,createdAt:now,updatedAt:now,snapshot,history:{storyVersions:[],aiCheckpoints:[]}};
 const next=applySavedCut(paper,snapshot,newCut.history);
 return {...next,assemblyCuts:{activeId:newId,items:[...cuts.items,newCut]}};
}
export function deleteAssemblyCut(paper,id,{now=new Date().toISOString()}={}){
 const cuts=synced(paper,now);
 if(cuts.items.length===1)throw new Error('Keep at least one working cut.');
 if(!cuts.items.some(item=>item.id===id))throw new Error('That Assembly cut could not be found.');
 const remaining=cuts.items.filter(item=>item.id!==id);
 if(cuts.activeId!==id)return {...paper,assemblyCuts:{...cuts,items:remaining}};
 const target=remaining[0];
 const next=applySavedCut(paper,target.snapshot,target.history);
 return {...next,assemblyCuts:{activeId:target.id,items:remaining}};
}
